import { useState, useEffect, useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, Link } from "@tanstack/react-router";
import { Lock, ArrowLeft, Eye, EyeOff, ShieldAlert } from "lucide-react";
import { apiFetch } from "@/lib/api";

const MAX_ATTEMPTS = 5;
const COOLDOWN_SECONDS = 30;

export default function Login() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [cooldown, setCooldown] = useState(0);

  const isLocked = cooldown > 0;

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          setAttempts(0);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const mutation = useMutation({
    mutationFn: () =>
      apiFetch<{ ok: boolean }>("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({ username, password }),
      }),
    onSuccess: () => {
      // Login response sets the session cookie, but the browser may not attach it to the very next
      // `fetch()` yet — `/api/auth/me` can briefly return `{ authenticated: false }` and look like failure.
      // The server session row already exists; trust success here and sync cache for the admin layout.
      queryClient.setQueryData(["auth", "me"], { authenticated: true });
      navigate({ to: "/admin/products" });
    },
    onError: () => {
      const next = attempts + 1;
      setAttempts(next);
      if (next >= MAX_ATTEMPTS) {
        setCooldown(COOLDOWN_SECONDS);
      }
    },
  });

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (isLocked || mutation.isPending) return;
      mutation.mutate();
    },
    [isLocked, mutation]
  );

  const remainingAttempts = MAX_ATTEMPTS - attempts;

  return (
    <div className="min-h-screen bg-[#111110] flex flex-col items-center justify-center px-4 relative overflow-hidden">
      {/* Ambient glow */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{
          background: "radial-gradient(circle, rgba(255,255,255,0.02) 0%, transparent 70%)",
        }}
      />

      {/* Grid texture */}
      <div
        className="absolute inset-0 opacity-[0.025] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,.15) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.15) 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />

      {/* Back to shop */}
      <Link
        to="/"
        className="absolute top-6 left-6 flex items-center gap-1.5 text-xs text-white/30 hover:text-white/60 transition-colors"
      >
        <ArrowLeft className="size-3.5" />
        Nazad na shop
      </Link>

      {/* Login card */}
      <div
        className="relative w-full max-w-[380px]"
        style={{ animation: "login-appear 0.6s cubic-bezier(0.16, 1, 0.3, 1) both" }}
      >
        {/* Card glow ring */}
        <div className="absolute -inset-px rounded-2xl bg-gradient-to-b from-white/[0.08] to-white/[0.02] pointer-events-none" />

        <div className="relative rounded-2xl bg-[#1a1a18] border border-white/[0.06] overflow-hidden">
          {/* Header */}
          <div className="px-8 pt-10 pb-6 text-center">
            {/* Lock icon */}
            <div className="inline-flex items-center justify-center size-12 rounded-xl bg-white/[0.04] border border-white/[0.06] mb-5">
              <Lock className="size-5 text-white/50" />
            </div>

            <h1 className="text-lg font-semibold text-white/90 tracking-tight">
              sosline
              <span className="text-white/30 font-normal ml-1.5">admin</span>
            </h1>
            <p className="text-[13px] text-white/25 mt-1.5">
              Prijavite se za pristup administraciji
            </p>
          </div>

          {/* Thin separator */}
          <div className="h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" />

          {/* Form */}
          <form onSubmit={handleSubmit} className="px-8 py-7 space-y-5">
            {/* Username */}
            <div className="space-y-2">
              <label htmlFor="username" className="text-[13px] font-medium text-white/40 block">
                Korisničko ime
              </label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                disabled={isLocked}
                autoComplete="username"
                className="w-full h-11 rounded-lg bg-white/[0.04] border border-white/[0.08] px-4 text-sm text-white/90 placeholder:text-white/15 focus:border-white/20 focus:bg-white/[0.06] focus:ring-1 focus:ring-white/10 outline-none transition-all disabled:opacity-40"
                placeholder="admin"
              />
            </div>

            {/* Password */}
            <div className="space-y-2">
              <label htmlFor="password" className="text-[13px] font-medium text-white/40 block">
                Lozinka
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isLocked}
                  autoComplete="current-password"
                  className="w-full h-11 rounded-lg bg-white/[0.04] border border-white/[0.08] px-4 pr-11 text-sm text-white/90 placeholder:text-white/15 focus:border-white/20 focus:bg-white/[0.06] focus:ring-1 focus:ring-white/10 outline-none transition-all disabled:opacity-40"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/20 hover:text-white/40 transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </button>
              </div>
            </div>

            {/* Error message */}
            {mutation.isError && !isLocked && (
              <div className="flex items-start gap-2.5 bg-red-500/[0.08] border border-red-500/[0.12] rounded-lg px-3.5 py-2.5">
                <div className="size-4 rounded-full bg-red-500/20 flex items-center justify-center mt-0.5 shrink-0">
                  <span className="text-red-400 text-[10px] font-bold">!</span>
                </div>
                <div>
                  <p className="text-[13px] text-red-400/90">
                    Pogrešno korisničko ime ili lozinka
                  </p>
                  {remainingAttempts <= 3 && remainingAttempts > 0 && (
                    <p className="text-[11px] text-red-400/50 mt-0.5">
                      Preostalo pokušaja: {remainingAttempts}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Lockout warning */}
            {isLocked && (
              <div className="flex items-center gap-3 bg-amber-500/[0.08] border border-amber-500/[0.12] rounded-lg px-3.5 py-3">
                <ShieldAlert className="size-5 text-amber-400/70 shrink-0" />
                <div>
                  <p className="text-[13px] text-amber-400/90 font-medium">
                    Previše neuspjelih pokušaja
                  </p>
                  <p className="text-[12px] text-amber-400/50 mt-0.5">
                    Pokušajte ponovo za{" "}
                    <span className="tabular-nums font-semibold text-amber-400/70">{cooldown}s</span>
                  </p>
                </div>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={isLocked || mutation.isPending}
              className="group relative w-full h-11 rounded-lg bg-white text-[#111110] text-sm font-semibold overflow-hidden transition-all hover:shadow-[0_0_20px_rgba(255,255,255,0.1)] disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-white via-neutral-100 to-white opacity-0 group-hover:opacity-100 transition-opacity" />
              <span className="relative flex items-center justify-center gap-2">
                {mutation.isPending ? (
                  <>
                    <span className="size-4 border-2 border-[#111110]/20 border-t-[#111110] rounded-full animate-spin" />
                    Prijavljivanje...
                  </>
                ) : isLocked ? (
                  <>
                    <Lock className="size-3.5" />
                    Zaključano ({cooldown}s)
                  </>
                ) : (
                  "Prijava"
                )}
              </span>
            </button>
          </form>
        </div>
      </div>

      {/* Footer */}
      <p className="mt-8 text-[11px] text-white/15 tracking-wide">
        sosline · admin panel
      </p>

      <style>{`
        @keyframes login-appear {
          from {
            opacity: 0;
            transform: translateY(16px) scale(0.98);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </div>
  );
}
